import { useMemo } from 'react';
import { normalizeModuleName } from '@flintmint/core-shared/medical-note';

import type { MedicalModuleWithId } from '@/services/medical-modules-service';

export const DUPLICATE_MODULE_NAME_ERROR = 'A module with this name already exists';

/**
 * Indexes module ids by normalized name for the uniqueness check. The index keeps its identity until a
 * module is added, removed or renamed, so memoized rows aren't re-rendered by content, flow or enabled writes.
 */
export const useModuleIdsByName = (modules: MedicalModuleWithId[]): ReadonlyMap<string, readonly string[]> => {
  // Keyed by modules identity first, so re-renders that don't touch the list (e.g. search keystrokes) skip the join.
  const namesKey = useMemo(() => modules.map((module) => `${module.id}\u0000${module.name}`).join('\u0001'), [modules]);

  // deps: namesKey captures every input the index is built from.
  return useMemo(() => indexModuleIdsByName(modules), [namesKey]);
};

/**
 * The classifier addresses modules by name, so a name must not repeat anywhere (DM-1544). Compared
 * trimmed and case-insensitively so near-duplicates the classifier could confuse are caught too.
 */
export const isModuleNameTaken = (
  name: string,
  moduleIdsByName: ReadonlyMap<string, readonly string[]>,
  ownId?: string
): boolean => (moduleIdsByName.get(normalizeModuleName(name)) ?? []).some((id) => id !== ownId);

const indexModuleIdsByName = (modules: MedicalModuleWithId[]): ReadonlyMap<string, readonly string[]> => {
  const index = new Map<string, string[]>();
  for (const module of modules) {
    const name = normalizeModuleName(module.name);
    index.set(name, [...(index.get(name) ?? []), module.id]);
  }
  return index;
};
